import { X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { useCompositionsList } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui/modal";

type ColorPickerPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onColorSelect: (color: string) => void;
  selectedColor?: string | undefined;
};

export function ColorPickerPopup({
  isOpen,
  onClose,
  onColorSelect,
  selectedColor
}: ColorPickerPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useResponsive();
  const { compositions } = useCompositionsList();

  const colors = useMemo(() => {
    // Create a map of colors to avoid duplicates
    const colorMap = new Map<string, { id: string; name: string; hex: string }>();

    compositions.forEach(comp => {
      const key = comp.colorName;
      if (!colorMap.has(key)) {
        colorMap.set(key, {
          id: comp.id,
          name: comp.colorName,
          hex: comp.colorHex
        });
      }
    });

    return Array.from(colorMap.values());
  }, [compositions]);

  const handleTShirtColorSelect = (colorHex: string) => {
    onColorSelect(colorHex);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDesktop && popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen && isDesktop) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isDesktop]);

  if (!isOpen) return null;

  const colorContent = (
    <div className="flex flex-col">
      <h3
        className={cn("font-semibold text-gray-500", isDesktop ? "mb-2 text-xs" : "mb-3 text-sm")}
      >
        Your T-shirt Colors
      </h3>
      <div
        className={cn(
          "flex flex-wrap overflow-y-auto p-1",
          isDesktop ? "max-h-[200px] gap-1.5" : "max-h-[300px] gap-2"
        )}
      >
        {colors.map(color => {
          const isSelected = selectedColor === color.hex;

          return (
            <button
              key={color.id}
              onClick={() => handleTShirtColorSelect(color.hex)}
              className={cn(
                "size-8 shrink-0 cursor-pointer rounded-full border transition-all hover:scale-105",
                isSelected ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"
              )}
              style={{ backgroundColor: color.hex }}
              title={color.name}
              data-testid={`color-picker-tshirt-${color.id}`}
            />
          );
        })}
      </div>
    </div>
  );

  if (!isDesktop) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="translate-none bottom-0 left-0 top-auto flex h-auto max-h-[90vh] w-full flex-col rounded-b-none"
        closeOnBackdropClick={true}
        closeOnEsc={true}
      >
        <ModalHeader onClose={onClose}>Pick Color</ModalHeader>
        <ModalBody className="flex-1 space-y-0 p-4">{colorContent}</ModalBody>
      </Modal>
    );
  }

  return (
    <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2">
      <div
        ref={popupRef}
        className="flex w-[350px] max-w-[90vw] flex-col rounded-lg bg-white p-4 shadow-xl"
      >
        <div className="relative mb-3 flex shrink-0 items-center justify-center">
          <h2 className="text-sm font-semibold text-gray-600">Pick Color</h2>
          <Button
            variant="icon"
            size="sm"
            onPress={onClose}
            className="absolute right-0 text-gray-400 hover:text-gray-600"
            data-testid="color-picker-close"
          >
            <X className="size-4.5" />
          </Button>
        </div>

        {colorContent}
      </div>
    </div>
  );
}
